import React, { useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, ArrowLeft, Loader2, Check } from "lucide-react";
import { http } from "@/lib/api";

/**
 * Register — two-step sign-up flow for new tenants.
 *
 * Step 1 captures the operator (your details) — name, email, password,
 * phone, role and how they heard about us. Step 2 captures the company
 * (workspace, headcount, industry, country, address, fleet size, and an
 * optional free-text use-case).
 *
 * Why two steps:
 *  - 12 fields on a single screen feels like a tax return. Splitting
 *    them lets people commit to "yes I'm signing up" with the easy 6
 *    fields before we ask for fleet-size details.
 *  - We can validate Step 1 BEFORE moving on, so the Stripe-bound
 *    "Continue to payment" button on Step 2 never fails on a missing
 *    name or weak password.
 *
 * All fields are required EXCEPT `use_case`. Free email providers are
 * blocked client-side; backend repeats the check (defense in depth).
 */

const FREE_DOMAINS_HINT = new Set([
    "gmail.com", "googlemail.com",
    "yahoo.com", "ymail.com",
    "outlook.com", "hotmail.com", "live.com", "msn.com",
    "icloud.com", "me.com", "mac.com",
    "aol.com", "proton.me", "protonmail.com", "pm.me",
    "zoho.com", "gmx.com", "mail.com", "yandex.com",
]);

function isFreeDomain(email) {
    const at = email.indexOf("@");
    if (at < 0) return false;
    return FREE_DOMAINS_HINT.has(email.slice(at + 1).trim().toLowerCase());
}

const REFERRAL_SOURCES = [
    "Search engine", "Referral / Word of mouth", "LinkedIn",
    "Conference / Event", "Existing customer", "Other",
];

const COMPANY_SIZES = ["1-10", "11-50", "51-200", "201-1000", "1000+"];

const INDUSTRIES = [
    "Technology / Software", "Financial Services", "Healthcare",
    "Legal", "Manufacturing", "Education", "Public sector",
    "Retail / E-commerce", "Media / Marketing", "Other",
];

const DEVICES_BUCKETS = ["1-25", "26-100", "101-500", "500+"];

// Fields we surface on Step 1 — used to short-circuit Next when something
// is empty so the user gets feedback BEFORE the network round-trip.
const STEP1_REQUIRED = ["name", "email", "password", "phone", "job_title", "referral_source"];
const STEP2_REQUIRED = ["tenant_name", "company_size", "industry", "country", "address", "devices_estimate"];

export default function Register() {
    const { user, register } = useAuth();
    // The Professional plan is retired (backend/services/plan_limits.py) --
    // self-serve signup is starter-only now; Enterprise is sales-
    // provisioned, not chosen at signup. A leftover ?plan=professional
    // link (old bookmark/email) is intentionally ignored rather than
    // honored -- every professional-conditional block below (PlanBadge,
    // the Stripe-checkout redirect, the $249.95 copy) is now permanently
    // unreachable as a result, not deleted piecemeal.
    const plan = "starter";

    const [step, setStep] = useState(1);
    const [form, setForm] = useState({
        // Step 1
        name: "",
        email: "",
        password: "",
        phone: "",
        job_title: "",
        referral_source: "",
        // Step 2
        tenant_name: "",
        company_size: "",
        industry: "",
        country: "",
        address: "",
        devices_estimate: "",
        use_case: "",
    });
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    const emailWarn = useMemo(
        () => (form.email && isFreeDomain(form.email) ? "Please use your work email." : ""),
        [form.email],
    );

    const change = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

    const step1Valid = useMemo(() => {
        if (emailWarn) return false;
        if ((form.password || "").length < 6) return false;
        return STEP1_REQUIRED.every((k) => (form[k] || "").trim().length > 0);
    }, [form, emailWarn]);

    const step2Valid = useMemo(
        () => STEP2_REQUIRED.every((k) => (form[k] || "").trim().length > 0),
        [form],
    );

    // Early-return AFTER all hooks (rules-of-hooks).
    if (user && plan !== "professional") return <Navigate to="/" replace />;

    const goNext = (e) => {
        e?.preventDefault?.();
        setErr("");
        if (!step1Valid) {
            setErr(emailWarn || "Please fill in every required field on this step.");
            return;
        }
        setStep(2);
    };

    const goBack = () => { setErr(""); setStep(1); };

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        if (!step1Valid) { setStep(1); setErr("Please complete the personal details first."); return; }
        if (!step2Valid) { setErr("Please fill in every required field."); return; }
        setLoading(true);
        try {
            const payload = {
                ...form,
                use_case: form.use_case?.trim() || undefined,
                plan,
            };
            const result = await register(payload);
            if (result?.requires_payment || plan === "professional") {
                const r = await http.post("/billing/start-trial", { origin_url: window.location.origin });
                if (r?.data?.url) {
                    window.location.href = r.data.url;
                    return;
                }
                setErr("Couldn't start checkout. Please try again from the Billing page.");
            }
        } catch (ex) {
            setErr(ex.message || "Couldn't create your account. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#03122b] px-6 py-10 text-white"
            data-testid="register-page"
        >
            <div
                className="pointer-events-none absolute inset-0 bg-cover bg-center opacity-[0.18]"
                style={{
                    backgroundImage:
                        "url(https://images.unsplash.com/photo-1639066648921-82d4500abf1a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxzZXJ2ZXIlMjByYWNrJTIwYWJzdHJhY3QlMjBkYXRhJTIwY2VudGVyfGVufDB8fHx8MTc3NjczNzU0N3ww&ixlib=rb-4.1.0&q=85)",
                }}
            />
            <div className="pointer-events-none absolute inset-0 grid-bg opacity-[0.14]" />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#03122b]/85 via-[#03122b]/95 to-[#03122b]" />
            <div className="pointer-events-none absolute -left-40 top-1/4 h-[500px] w-[500px] rounded-full bg-[#00C2FF]/12 blur-[160px]" />
            <div className="pointer-events-none absolute -right-40 bottom-1/4 h-[440px] w-[440px] rounded-full bg-[#5b5ef0]/14 blur-[160px]" />

            <div className="relative z-10 w-full max-w-2xl">
                <div className="mb-6 flex items-center justify-center">
                    <Link to="/" data-testid="register-logo-link" aria-label="Back to home">
                        <img
                            src="/brand/moovbytes-logo-white.png"
                            alt="MoovBytes"
                            className="h-8 w-auto select-none transition hover:opacity-80"
                            data-testid="register-logo"
                        />
                    </Link>
                </div>

                <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] p-7 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] backdrop-blur-xl sm:p-9">
                    <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#00C2FF]/60 to-transparent" />

                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <PlanBadge plan={plan} />
                        <StepIndicator step={step} />
                    </div>

                    <h1 className="mt-4 font-display text-2xl font-bold tracking-tight sm:text-3xl" data-testid="register-step-heading">
                        {step === 1 ? "Tell us about you" : "Tell us about your company"}
                    </h1>
                    <p className="mt-2 text-sm text-white/60">
                        {step === 1
                            ? "Use your work email — free providers (Gmail, Yahoo, Outlook…) aren't supported."
                            : "Helps us right-size your migration plan and route data to the correct region."}
                    </p>

                    <form
                        onSubmit={step === 1 ? goNext : submit}
                        className="mt-7 space-y-4"
                        data-testid="register-form"
                        noValidate
                    >
                        {step === 1 && (
                            <Step1Fields form={form} change={change} emailWarn={emailWarn} />
                        )}
                        {step === 2 && (
                            <Step2Fields form={form} change={change} />
                        )}

                        {err && (
                            <div
                                data-testid="register-error"
                                className="rounded-lg border border-[#ef4444]/40 bg-[#ef4444]/10 px-3 py-2 text-xs text-[#fca5a5]"
                            >
                                {err}
                            </div>
                        )}

                        <div className="flex flex-wrap items-center gap-3 pt-1">
                            {step === 2 && (
                                <button
                                    type="button"
                                    onClick={goBack}
                                    disabled={loading}
                                    data-testid="register-back-btn"
                                    className="inline-flex items-center gap-2 rounded-lg border border-white/15 px-4 py-3 text-sm font-semibold uppercase tracking-widest text-white/75 transition hover:border-white/40 hover:text-white disabled:opacity-50"
                                >
                                    <ArrowLeft size={14} /> Back
                                </button>
                            )}
                            <button
                                type="submit"
                                disabled={loading || (step === 1 ? !!emailWarn : false)}
                                data-testid={step === 1 ? "register-next-btn" : "register-submit-btn"}
                                className="group inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#00C2FF] to-[#5b5ef0] px-3 py-3 text-sm font-semibold uppercase tracking-widest text-white shadow-lg shadow-[#004AAD]/30 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {loading && <Loader2 size={14} className="animate-spin" />}
                                {step === 1
                                    ? "Continue"
                                    : plan === "professional"
                                        ? (loading ? "Redirecting to checkout…" : "Continue to payment")
                                        : (loading ? "Creating account…" : "Create my account")}
                                {!loading && (
                                    <ArrowRight size={14} className="transition group-hover:translate-x-0.5" />
                                )}
                            </button>
                        </div>

                        {step === 2 && plan === "professional" && (
                            <p className="text-center text-[11px] text-white/50">
                                Next step: Stripe checkout for the $249.95 USD initial cost.
                                Extra devices beyond 50 are billed at $9.99 USD each. Card
                                details stay on Stripe — we never see them.
                            </p>
                        )}
                    </form>

                    <div className="mt-6 text-center text-xs text-white/55">
                        Already have an account?{" "}
                        <Link
                            to="/login"
                            className="font-mono uppercase tracking-[0.22em] text-[#00C2FF] transition hover:text-white"
                            data-testid="link-login"
                        >
                            Log in
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ---- step bodies -----------------------------------------------------

function Step1Fields({ form, change, emailWarn }) {
    return (
        <div className="space-y-4" data-testid="register-step-1">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Full name *">
                    <input
                        type="text" required value={form.name} onChange={change("name")}
                        data-testid="register-name-input" className={inputCls}
                    />
                </Field>
                <Field label="Work email *" error={emailWarn} testid="register-email-warn">
                    <input
                        type="email" required value={form.email} onChange={change("email")}
                        data-testid="register-email-input" className={inputCls}
                    />
                </Field>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Password (min 6) *">
                    <input
                        type="password" required minLength={6} value={form.password} onChange={change("password")}
                        data-testid="register-password-input" className={inputCls}
                    />
                </Field>
                <Field label="Phone *">
                    <input
                        type="tel" required value={form.phone} onChange={change("phone")}
                        placeholder="+1 (555) 123-4567"
                        data-testid="register-phone-input" className={inputCls}
                    />
                </Field>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Job title *">
                    <input
                        type="text" required value={form.job_title} onChange={change("job_title")}
                        placeholder="IT Manager, CTO, Sysadmin…"
                        data-testid="register-jobtitle-input" className={inputCls}
                    />
                </Field>
                <Field label="How did you hear about us? *">
                    <select
                        required value={form.referral_source} onChange={change("referral_source")}
                        data-testid="register-referral-select" className={selectCls}
                    >
                        <option value="">Select one…</option>
                        {REFERRAL_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </Field>
            </div>
        </div>
    );
}

function Step2Fields({ form, change }) {
    return (
        <div className="space-y-4" data-testid="register-step-2">
            <Field label="Workspace / Company *">
                <input
                    type="text" required value={form.tenant_name} onChange={change("tenant_name")}
                    data-testid="register-tenant-input" className={inputCls}
                />
            </Field>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Company size *">
                    <select
                        required value={form.company_size} onChange={change("company_size")}
                        data-testid="register-companysize-select" className={selectCls}
                    >
                        <option value="">Select one…</option>
                        {COMPANY_SIZES.map((s) => <option key={s} value={s}>{s} employees</option>)}
                    </select>
                </Field>
                <Field label="Industry *">
                    <select
                        required value={form.industry} onChange={change("industry")}
                        data-testid="register-industry-select" className={selectCls}
                    >
                        <option value="">Select one…</option>
                        {INDUSTRIES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </Field>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Country *">
                    <input
                        type="text" required value={form.country} onChange={change("country")}
                        placeholder="United States, Canada, Germany…"
                        data-testid="register-country-input" className={inputCls}
                    />
                </Field>
                <Field label="Devices to migrate *">
                    <select
                        required value={form.devices_estimate} onChange={change("devices_estimate")}
                        data-testid="register-devices-select" className={selectCls}
                    >
                        <option value="">Select range…</option>
                        {DEVICES_BUCKETS.map((s) => <option key={s} value={s}>{s} devices</option>)}
                    </select>
                </Field>
            </div>

            <Field label="Physical address *">
                <textarea
                    required rows={2} value={form.address} onChange={change("address")}
                    placeholder="Street, city, state/province, postal code"
                    data-testid="register-address-input" className={inputCls}
                />
            </Field>

            <Field label="What do you plan to migrate? (optional)">
                <textarea
                    rows={2} value={form.use_case} onChange={change("use_case")}
                    placeholder="E.g. 250 Windows workstations from on-prem AD to Entra; archive 40TB NAS to S3…"
                    data-testid="register-usecase-input" className={inputCls}
                />
            </Field>
        </div>
    );
}

// ---- chrome ----------------------------------------------------------

function StepIndicator({ step }) {
    return (
        <div className="flex items-center gap-2" data-testid="register-step-indicator">
            <Pill active={step === 1} done={step > 1} num={1} label="You" />
            <span className="h-px w-6 bg-white/15" />
            <Pill active={step === 2} done={false} num={2} label="Company" />
        </div>
    );
}

function Pill({ active, done, num, label }) {
    return (
        <div
            className={[
                "flex items-center gap-1.5 rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.22em] transition",
                done
                    ? "border-[#00C2FF]/50 bg-[#00C2FF]/15 text-[#7fe0ff]"
                    : active
                        ? "border-[#00C2FF]/60 bg-[#00C2FF]/10 text-white"
                        : "border-white/15 bg-white/[0.03] text-white/45",
            ].join(" ")}
        >
            <span className={[
                "flex h-4 w-4 items-center justify-center rounded-full text-[9px]",
                done ? "bg-[#00C2FF] text-[#03122b]" : active ? "bg-white text-[#03122b]" : "bg-white/15 text-white/60",
            ].join(" ")}>
                {done ? <Check size={9} strokeWidth={3} /> : num}
            </span>
            {label}
        </div>
    );
}

function PlanBadge({ plan }) {
    if (plan === "professional") {
        return (
            <div
                data-testid="register-plan-badge"
                className="inline-flex items-center gap-2 rounded-full border border-[#5b5ef0]/40 bg-[#5b5ef0]/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.26em] text-[#cfe9ff]"
            >
                <span className="h-1.5 w-1.5 rounded-full bg-[#5b5ef0]" />
                Professional plan · $249.95 today
            </div>
        );
    }
    return (
        <div
            data-testid="register-plan-badge"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-3 py-1 font-mono text-[10px] uppercase tracking-[0.26em] text-[#00C2FF]"
        >
            <span className="h-1.5 w-1.5 rounded-full bg-[#00C2FF]" />
            Starter plan · always free
        </div>
    );
}

const inputCls =
    "w-full rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2.5 text-sm text-white placeholder:text-white/30 outline-none transition focus:border-[#00C2FF]/60 focus:bg-white/[0.08] focus:ring-2 focus:ring-[#00C2FF]/20";

const selectCls = inputCls + " appearance-none pr-8";

function Field({ label, error, testid, children }) {
    return (
        <label className="block">
            <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-white/45">
                {label}
            </span>
            {children}
            {error && (
                <span
                    data-testid={testid}
                    className="mt-1 block font-mono text-[10px] uppercase tracking-[0.18em] text-[#fca5a5]"
                >
                    {error}
                </span>
            )}
        </label>
    );
}
